<!-- EchartsPieChart.vue -->
 <!-- 扇形区域是圆角的 -->
<!-- 饼图 -->
<template>
  <div class="pie-chart-container">
    <div ref="chart" :style="{ width: width, height: height }"></div>
  </div>
</template>

<script>
/* eslint-disable no-undef, vue/no-mutating-props */
import * as echarts from 'echarts'

export default {
  name: 'EchartsPieChart',
  
  props: {
    title: {
      type: String,
      default: '饼图统计'
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    chartData: {
      type: Array,
      default: () => [
        { value: 1048, name: '搜索引擎' },
        { value: 735, name: '直接访问' },
        { value: 580, name: '邮件营销' },
        { value: 484, name: '联盟广告' },
        { value: 300, name: '视频广告' }
      ]
    },
    height: {
      type: String,
      default: '100%'
    },
    width: {
      type: String,
      default: '100%'
    },
    radius: {
      type: Array,
      default: () => ['40%', '70%']
    },
    center: {
      type: Array,
      default: () => ['50%', '50%']
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    legendPosition: {
      type: String,
      default: 'top',
      validator: (value) => ['top', 'bottom', 'left', 'right'].includes(value)
    },
    // 新增下载相关属性
    downloadFileName: {
      type: String,
      default: 'pie_chart'
    },
    downloadType: {
      type: String,
      default: 'png',
      validator: (value) => ['png', 'jpeg'].includes(value)
    },
    // 是否显示工具箱
    showToolbox: {
      type: Boolean,
      default: true
    },
    // 下载时使用的背景色
    downloadBackgroundColor: {
      type: String,
      default: '#ffffff'
    },
    // 颜色序列配置
    colors: {
      type: Array,
      default: () => [
        '#5470c6', '#91cc75', '#fac858', '#ee6666', 
        '#73c0de', '#3ba272', '#fc8452', '#9a60b4',
        '#ea7ccc', '#7ebf42', '#e6d450', '#f2a7c3',
        '#b6a2de', '#5ab1ef', '#ffb980', '#d87a80'
      ]
    },
    // 是否在数据中优先使用自定义颜色
    useDataColorPriority: {
      type: Boolean,
      default: true
    },
    // 图例文字颜色
    legendTextColor: {
      type: String,
      default: '#333'
    },
    // 扇区边框颜色
    borderColor: {
      type: String,
      default: '#fff'
    },
    // 扇区边框宽度
    borderWidth: {
      type: Number,
      default: 2
    },
    // 是否显示饼图标签
    showLabel: {
      type: Boolean,
      default: true
    },
    // 标签位置
    labelPosition: {
      type: String,
      default: 'outside',
      validator: (value) => ['inside', 'outside', 'center'].includes(value)
    },
    // 高亮时扇区偏移距离
    selectedOffset: {
      type: Number,
      default: 10
    },
    // 选中模式
    selectedMode: {
      type: String,
      default: 'single',
      validator: (value) => ['single', 'multiple', false].includes(value)
    },
    // 新增：扇区圆角半径
    borderRadius: {
      type: Number,
      default: 10
    },
    // 新增：高亮时扇区圆角半径
    emphasisBorderRadius: {
      type: Number,
      default: 10
    },
    // 新增：扇区阴影效果
    shadowBlur: {
      type: Number,
      default: 0
    },
    // 新增：是否显示扇区阴影
    showShadow: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      chartInstance: null
    }
  },
  
  computed: {
    // 计算总数用于百分比计算
    total() {
      return this.chartData.reduce((sum, item) => sum + item.value, 0)
    }
  },
  
  methods: {
    // 准备图表数据，应用颜色和样式
    prepareChartData() {
      return this.chartData.map((item, index) => {
        const dataItem = {
          value: item.value || 0, // 确保有值
          name: item.name || `数据${index + 1}`
        };
        
        // 确定使用哪种颜色
        let finalColor = null;
        
        // 优先使用数据中的颜色（如果启用优先级）
        if (this.useDataColorPriority && item.color) {
          finalColor = item.color;
        }
        // 否则使用颜色序列中的颜色
        else if (this.colors && this.colors.length > 0) {
          finalColor = this.colors[index % this.colors.length];
        }
        
        // 如果数据中有颜色但优先级未启用，也使用数据中的颜色
        if (!finalColor && item.color) {
          finalColor = item.color;
        }
        
        // 确保有颜色
        if (!finalColor && this.colors && this.colors.length > 0) {
          finalColor = this.colors[index % this.colors.length];
        }
        
        // 设置扇区样式 - 包含圆角效果
        dataItem.itemStyle = {
          color: finalColor || '#5470c6', // 默认颜色
          borderRadius: this.borderRadius, // 圆角半径
          borderColor: this.borderColor,
          borderWidth: this.borderWidth
        };
        
        // 添加阴影效果
        if (this.showShadow) {
          dataItem.itemStyle.shadowBlur = this.shadowBlur;
          dataItem.itemStyle.shadowColor = 'rgba(0, 0, 0, 0.3)';
          dataItem.itemStyle.shadowOffsetX = 0;
          dataItem.itemStyle.shadowOffsetY = 2;
        }
        
        // 设置高亮状态
        dataItem.emphasis = {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
            borderColor: this.borderColor,
            borderWidth: this.borderWidth,
            borderRadius: this.emphasisBorderRadius // 高亮时的圆角
          }
        };
        
        // 如果高亮时也显示阴影
        if (this.showShadow) {
          dataItem.emphasis.itemStyle.shadowBlur = Math.max(this.shadowBlur, 10);
        }
        
        return dataItem;
      });
    },

    // 下载图片
    downloadImage() {
      if (!this.chartInstance) {
        console.warn('图表实例未初始化');
        return false;
      }

      try {
        const dataURL = this.chartInstance.getDataURL({
          type: this.downloadType,
          pixelRatio: 2,
          backgroundColor: this.downloadBackgroundColor,
          excludeComponents: ['toolbox']
        });

        const link = document.createElement('a');
        link.href = dataURL;
        link.download = `${this.downloadFileName}_${new Date().getTime()}.${this.downloadType}`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        console.log('饼图下载成功');
        return true;
      } catch (error) {
        console.error('饼图下载失败:', error);
        alert('下载失败，请重试');
        return false;
      }
    },

    // 获取工具箱配置
    getToolboxConfig() {
      if (!this.showToolbox) {
        return undefined;
      }
      
      return {
        feature: {
          saveAsImage: {
            title: '保存为图片',
            type: this.downloadType,
            name: this.downloadFileName,
            pixelRatio: 2,
            backgroundColor: this.downloadBackgroundColor,
            excludeComponents: ['toolbox'],
          }
        },
        right: 10,
        top: 10,
        iconStyle: {
          borderColor: '#666'
        }
      };
    },

    // 获取图例配置
    getLegendConfig() {
      const positionMap = {
        top: { top: '10%', left: 'center' },
        bottom: { bottom: '10%', left: 'center' },
        left: { left: '10%', top: 'center', orient: 'vertical' },
        right: { right: '10%', top: 'center', orient: 'vertical' }
      }

      const baseConfig = positionMap[this.legendPosition]
      
      return this.showLegend ? {
        ...baseConfig,
        formatter: (name) => {
          //const dataItem = this.chartData.find(item => item.name === name)
          // if (dataItem) {
          //   const percentage = ((dataItem.value / this.total) * 100).toFixed(1)
          //   return `${name}: ${dataItem.value} (${percentage}%)`
          // }
          return name
        },
        textStyle: {
          fontSize: 12,
          color: this.legendTextColor,
          lineHeight: 16
        },
        // 图例选择交互
        selectedMode: true,
        // 图标样式
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 10
      } : { show: false }
    },

    // 获取标签配置
    getLabelConfig() {
      const labelConfig = {
        show: this.showLabel,
        position: this.labelPosition,
        formatter: (params) => {
          const percentage = ((params.value / this.total) * 100).toFixed(1);
          return `${params.name}\n${params.value} (${percentage}%)`;
        },
        textStyle: {
          fontSize: 12,
          fontWeight: 'normal'
        }
      };

      // 根据不同位置调整标签样式
      if (this.labelPosition === 'inside') {
        labelConfig.textStyle.color = '#fff';
        labelConfig.textStyle.fontWeight = 'bold';
        labelConfig.textStyle.textShadow = '0 1px 2px rgba(0,0,0,0.5)';
      } else if (this.labelPosition === 'center') {
        labelConfig.textStyle.fontSize = 16;
        labelConfig.textStyle.fontWeight = 'bold';
        labelConfig.formatter = '{d}%';
      }

      return labelConfig;
    },

    // 获取标签线配置
    getLabelLineConfig() {
      if (this.labelPosition === 'outside') {
        return {
          show: true,
          length: 10,
          length2: 10,
          smooth: true,
          lineStyle: {
            width: 1,
            type: 'solid'
          }
        };
      }
      return { show: false };
    },

    // 初始化图表配置
    getChartOption() {
      // 准备带有颜色的数据
      const preparedData = this.prepareChartData();
      
      // 检查是否有有效数据
      const hasValidData = preparedData.some(item => item.value > 0);
      if (!hasValidData) {
        console.warn('没有有效数据，显示默认提示');
        preparedData.push({
          value: 1,
          name: '暂无数据',
          itemStyle: {
            color: '#f0f0f0',
            borderRadius: this.borderRadius,
            borderColor: '#ddd',
            borderWidth: this.borderWidth
          }
        });
      }
      
      return {
        // 设置全局颜色序列 - 这会影响图例标记颜色
        color: this.colors,
        // 网格配置确保图表区域充分利用空间
        grid: {
          top: this.showLegend ? '25%' : '10%',
          right: '10%',
          bottom: '10%',
          left: '10%',
          containLabel: true
        },
        tooltip: {
          trigger: 'item',
          formatter: (params) => {
            // 如果是"暂无数据"提示
            if (params.name === '暂无数据') {
              return '暂无有效数据';
            }
            
            const percentage = ((params.value / this.total) * 100).toFixed(1);
            // 添加颜色块到tooltip
            return `
              <div style="display: flex; align-items: center;">
                <div style="width: 12px; height: 12px; background: ${params.color}; margin-right: 8px; border-radius: 2px;"></div>
                <div>
                  <strong>${params.name}</strong><br/>
                  数值: ${params.value}<br/>
                  占比: ${percentage}%
                </div>
              </div>
            `;
          }
        },
        legend: this.getLegendConfig(),
        // 添加工具箱配置
        toolbox: this.getToolboxConfig(),
        series: [
          {
            name: this.title,
            type: 'pie',
            radius: this.radius,
            center: this.center,
            avoidLabelOverlap: true,
            selectedMode: this.selectedMode,
            selectedOffset: this.selectedOffset,
            // 在series层级也设置圆角，确保生效
            itemStyle: {
              borderRadius: this.borderRadius,
              borderColor: this.borderColor,
              borderWidth: this.borderWidth
            },
            label: this.getLabelConfig(),
            labelLine: this.getLabelLineConfig(),
            emphasis: {
              scale: true,
              scaleSize: 5,
              itemStyle: {
                borderRadius: this.emphasisBorderRadius,
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              },
              label: {
                show: true,
                fontSize: 16,
                fontWeight: 'bold',
                formatter: (params) => {
                  // 如果是"暂无数据"提示
                  if (params.name === '暂无数据') {
                    return '暂无数据';
                  }
                  const percentage = ((params.value / this.total) * 100).toFixed(1);
                  return `${params.name}\n${params.value} (${percentage}%)`;
                }
              }
            },
            data: preparedData
          }
        ]
      }
    },

    initChart() {
      if (!this.$refs.chart) return

      // 检查容器是否有尺寸
      const rect = this.$refs.chart.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) {
        console.log('图表容器尺寸为0，延迟初始化')
        setTimeout(() => this.initChart(), 100)
        return
      }

      const option = this.getChartOption()

      if (!this.chartInstance) {
        this.chartInstance = echarts.init(this.$refs.chart)
      }
      
      this.chartInstance.setOption(option, true)
    },

    handleResize() {
      if (this.chartInstance) {
        this.chartInstance.resize()
      }
    },

    // 获取图表实例
    getInstance() {
      return this.chartInstance;
    },

    // 手动更新颜色
    updateColors(newColors) {
      if (newColors && Array.isArray(newColors)) {
        this.colors = newColors;
        this.$nextTick(() => {
          this.initChart();
        });
      }
    },

    // 手动设置数据项颜色
    setDataColor(name, color) {
      const dataIndex = this.chartData.findIndex(item => item.name === name);
      if (dataIndex !== -1) {
        // 注意：直接修改props可能有问题，这里只是示例
        // 在实际应用中可能需要通过emit通知父组件修改数据
        this.chartData[dataIndex].color = color;
        this.$nextTick(() => {
          this.initChart();
        });
      }
    },

    // 刷新图表
    refresh() {
      this.initChart();
    }
  },
  
  mounted() {
    this.$nextTick(() => {
      setTimeout(() => {
        this.initChart()
      }, 300)
    })
    
    window.addEventListener('resize', this.handleResize)
  },
  
  beforeUnmount() {
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
    window.removeEventListener('resize', this.handleResize)
  },
  
  watch: {
    chartData: {
      handler() {
        this.$nextTick(() => {
          this.initChart()
        })
      },
      deep: true
    },
    radius() {
      this.$nextTick(() => {
        this.initChart()
      })
    },
    center() {
      this.$nextTick(() => {
        this.initChart()
      })
    },
    showLegend() {
      this.$nextTick(() => {
        this.initChart()
      })
    },
    legendPosition() {
      this.$nextTick(() => {
        this.initChart()
      })
    },
    showToolbox() {
      this.$nextTick(() => {
        this.initChart()
      })
    },
    // 颜色相关属性的监听
    colors: {
      handler() {
        this.$nextTick(() => {
          this.initChart()
        })
      },
      deep: true
    },
    legendTextColor() {
      this.$nextTick(() => {
        this.initChart()
      })
    },
    borderColor() {
      this.$nextTick(() => {
        this.initChart()
      })
    },
    borderWidth() {
      this.$nextTick(() => {
        this.initChart()
      })
    },
    showLabel() {
      this.$nextTick(() => {
        this.initChart()
      })
    },
    labelPosition() {
      this.$nextTick(() => {
        this.initChart()
      })
    },
    selectedOffset() {
      this.$nextTick(() => {
        this.initChart()
      })
    },
    selectedMode() {
      this.$nextTick(() => {
        this.initChart()
      })
    },
    useDataColorPriority() {
      this.$nextTick(() => {
        this.initChart()
      })
    },
    // 圆角相关属性的监听
    borderRadius() {
      this.$nextTick(() => {
        this.initChart()
      })
    },
    emphasisBorderRadius() {
      this.$nextTick(() => {
        this.initChart()
      })
    },
    // 阴影相关属性的监听
    shadowBlur() {
      this.$nextTick(() => {
        this.initChart()
      })
    },
    showShadow() {
      this.$nextTick(() => {
        this.initChart()
      })
    }
  },
  
  // 暴露给父组件的方法
  expose: [
    'downloadImage', 
    'handleResize', 
    'getInstance', 
    'initChart', 
    'updateColors', 
    'setDataColor',
    'refresh'
  ]
}
</script>

<style scoped>
.pie-chart-container {
  width: 100%;
  height: 100%;
  position: relative;
}
</style>